<template>
    <div>
      <SettingHeaderTab />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Tax List</h5>
          <p class="page-sub">Manage tax rates applied to your products and services</p>
        </div>
        <div class="page-actions">
          <div class="search-wrap">
            <font-awesome-icon icon="search" class="search-icon" />
            <input
              type="text"
              class="search-input"
              placeholder="Search taxes..."
              v-model="query"
            />
          </div>
          <button
            class="btn-primary-action"
            :disabled="permission.create_taxes != 1"
            :class="{ 'btn--disabled': permission.create_taxes != 1 }"
            @click="openCreate"
          >
            <font-awesome-icon icon="plus" /> Create Tax
          </button>
        </div>
      </div>
  
      <!-- Data card -->
      <div class="data-card">
  
        <!-- Loading -->
        <div v-if="loading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading taxes...</p>
        </div>
  
        <!-- 403 -->
        <div v-else-if="permission_status === 403" class="state-block">
          <div class="state-icon state-icon--red"><font-awesome-icon icon="lock" /></div>
          <p class="state-title">Access Denied</p>
          <p class="state-sub">You don't have permission to view taxes.</p>
        </div>
  
        <!-- Empty -->
        <div v-else-if="taxes.length === 0" class="state-block">
          <div class="state-icon state-icon--blue"><font-awesome-icon icon="receipt" /></div>
          <p class="state-title">No taxes found</p>
          <p class="state-sub">
            {{ query ? 'No results match your search.' : 'Create your first tax rate to get started.' }}
          </p>
          <button
            v-if="!query && permission.create_taxes == 1"
            class="btn-primary-action mt-3"
            @click="openCreate"
          >
            <font-awesome-icon icon="plus" /> Create Tax
          </button>
        </div>
  
        <template v-else>
  
          <!-- Table -->
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tax Name</th>
                  <th class="text-center">Rate (%)</th>
                  <th class="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(tax, index) in taxes" :key="tax.id">
  
                  <td class="text-muted-cell">{{ index + 1 }}</td>
  
                  <td>
                    <div class="tax-cell">
                      <div class="tax-icon"><font-awesome-icon icon="receipt" /></div>
                      <span class="tax-name">{{ tax.tax_name }}</span>
                    </div>
                  </td>
  
                  <td class="text-center">
                    <span class="rate-badge">{{ tax.tax_percentage }}%</span>
                  </td>
  
                  <td class="col-actions">
                    <div class="actions-cell">
                      <button
                        class="action-btn action-btn--edit"
                        :class="{ 'action-btn--disabled': !canEdit(tax) }"
                        :disabled="!canEdit(tax)"
                        title="Edit"
                        @click="openEdit(tax)"
                      >
                        <font-awesome-icon icon="pen" />
                      </button>
                      <button
                        class="action-btn action-btn--delete"
                        :class="{ 'action-btn--disabled': !canDelete(tax) }"
                        :disabled="!canDelete(tax)"
                        title="Delete"
                        @click="openDelete(tax)"
                      >
                        <font-awesome-icon icon="trash" />
                      </button>
                    </div>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
        </template>
      </div>
  
      <!-- ── Create Modal ── -->
      <div class="modal-overlay" :class="{ open: createOpen }" @click.self="closeCreate">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Create Tax</span>
            <button class="modal-close-btn" @click="closeCreate"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="createTax">
            <div class="modal-body-styled">
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Tax Name <span class="req">*</span></label>
                  <input
                    ref="createInput"
                    v-model="ctax_name"
                    type="text"
                    class="form-input-styled"
                    placeholder="e.g. VAT, Sales Tax"
                    required
                  />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Rate (%) <span class="req">*</span></label>
                  <div class="input-with-suffix">
                    <input
                      v-model="ctax_percentage"
                      type="number"
                      class="form-input-styled"
                      placeholder="e.g. 7.5"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeCreate">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!ctax_name.trim() || !ctax_percentage || loading">
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Creating...' : 'Create Tax' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- ── Edit Modal ── -->
      <div class="modal-overlay" :class="{ open: editOpen }" @click.self="closeEdit">
        <div class="modal-box">
          <div class="modal-header-styled">
            <span class="modal-title-styled">Edit Tax</span>
            <button class="modal-close-btn" @click="closeEdit"><font-awesome-icon icon="times" /></button>
          </div>
          <form @submit.prevent="updateTax">
            <div class="modal-body-styled">
              <div class="fields-row">
                <div class="form-group-styled">
                  <label class="form-label-styled">Tax Name <span class="req">*</span></label>
                  <input
                    v-model="tax_name"
                    type="text"
                    class="form-input-styled"
                    placeholder="Tax name"
                    required
                  />
                </div>
                <div class="form-group-styled">
                  <label class="form-label-styled">Rate (%) <span class="req">*</span></label>
                  <div class="input-with-suffix">
                    <input
                      v-model="tax_percentage"
                      type="number"
                      class="form-input-styled"
                      placeholder="e.g. 7.5"
                      min="0"
                      max="100"
                      step="0.01"
                      required
                    />
                    <span class="input-suffix">%</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer-styled">
              <button type="button" class="btn-ghost" @click="closeEdit">Cancel</button>
              <button type="submit" class="btn-primary-action" :disabled="!tax_name.trim() || !tax_percentage || crudLoading">
                <font-awesome-icon v-if="crudLoading" icon="spinner" spin class="me-1" />
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
  
      <!-- ── Delete Modal ── -->
      <div class="modal-overlay" :class="{ open: deleteOpen }" @click.self="closeDelete">
        <div class="modal-box">
          <div class="modal-body-styled" style="padding:32px 24px 24px; text-align:center;">
            <div class="delete-icon-wrap"><font-awesome-icon icon="trash" style="font-size:26px;" /></div>
            <div class="delete-title">Delete Tax</div>
            <p class="delete-sub">
              Are you sure you want to delete <strong>{{ tax_name }}</strong>? This action cannot be undone.
            </p>
          </div>
          <div class="modal-footer-styled" style="justify-content:center; gap:12px;">
            <button type="button" class="btn-ghost" @click="closeDelete">Cancel</button>
            <button type="button" class="btn-danger-action" @click="deleteTax">Yes, Delete</button>
          </div>
        </div>
      </div>
  
      <!-- Backdrop -->
      <div v-if="createOpen || editOpen || deleteOpen" class="modal-backdrop-custom" />
  
    </div>
  </template>
  
  <script>
  import { ref, computed, watch, nextTick, onMounted } from 'vue';
  import { useStore } from 'vuex';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import SettingHeaderTab from './components/settingheadertab.vue';
  
  export default {
    name: 'TaxList',
    components: { SettingHeaderTab },
  
    setup() {
      const store = useStore();
      const toast = useToast();
  
      // ── State ────────────────────────────────────────────────────────
      const taxes           = ref([]);
      const loading         = ref(false);
      const crudLoading     = ref(false);
      const permission_status = ref(null);
      const query           = ref('');
      const tax_id          = ref('');
      const tax_name        = ref('');
      const tax_percentage  = ref('');
      const ctax_name       = ref('');
      const ctax_percentage = ref('');
      const createOpen      = ref(false);
      const editOpen        = ref(false);
      const deleteOpen      = ref(false);
      const createInput     = ref(null);
  
      const permission = computed(() => store.state.getCurrentUserPermission);
      const canEdit   = (tax) => tax.can_delete && permission.value.delete_taxes == 1;
      const canDelete = (tax) => tax.can_delete && permission.value.delete_taxes == 1;
  
      // ── Modal helpers ────────────────────────────────────────────────
      const openCreate = async () => {
        ctax_name.value = ''; ctax_percentage.value = '';
        createOpen.value = true;
        await nextTick(); createInput.value?.focus();
      };
      const closeCreate = () => { createOpen.value = false; };
  
      const openEdit = (tax) => {
        tax_id.value         = tax.id;
        tax_name.value       = tax.tax_name;
        tax_percentage.value = tax.tax_percentage;
        editOpen.value       = true;
      };
      const closeEdit = () => { editOpen.value = false; };
  
      const openDelete = (tax) => {
        tax_id.value   = tax.id;
        tax_name.value = tax.tax_name;
        deleteOpen.value = true;
      };
      const closeDelete = () => { deleteOpen.value = false; };
  
      // ── Data ─────────────────────────────────────────────────────────
      const getAllTaxes = async () => {
        loading.value = true;
        try {
          const { data } = await axios.get('settings/tax', { params: { query: query.value } });
          taxes.value = data.data;
        } catch (err) {
          if (err?.response?.status === 403) {
            permission_status.value = 403;
            toast.error(err.response.data.message);
          } else {
            toast.error('Could not load taxes.');
          }
        } finally {
          loading.value = false;
        }
      };
  
      // ── CRUD ─────────────────────────────────────────────────────────
      const createTax = async () => {
        loading.value = true;
        try {
          await axios.post('/settings/tax', { tax_name: ctax_name.value, tax_percentage: ctax_percentage.value });
          toast.success('Tax created successfully.');
          closeCreate();
          getAllTaxes();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) toast.warning(err.response.data.message?.[0] ?? 'Invalid input.');
          else if (status === 403) toast.warning(err.response.data.message);
          else toast.error('An error occurred. Please try again.');
        } finally {
          loading.value = false;
        }
      };
  
      const updateTax = async () => {
        crudLoading.value = true;
        try {
          await axios.put(`/settings/tax/${tax_id.value}`, { tax_name: tax_name.value, tax_percentage: tax_percentage.value });
          toast.success('Tax updated successfully.');
          closeEdit();
          getAllTaxes();
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) toast.warning(err.response.data.message?.[0] ?? 'Invalid input.');
          else toast.error('An error occurred. Please try again.');
        } finally {
          crudLoading.value = false;
        }
      };
  
      const deleteTax = async () => {
        try {
          const { data } = await axios.delete(`/settings/tax/${tax_id.value}`);
          toast.success(data.message);
          closeDelete();
          getAllTaxes();
        } catch (err) {
          const status = err.response?.status;
          if (status === 400) toast.error(err.response.data.message);
          else toast.error('Tax could not be deleted.');
        }
      };
  
      watch(query, getAllTaxes);
      onMounted(getAllTaxes);
  
      return {
        taxes, loading, crudLoading, permission_status, query,
        tax_id, tax_name, tax_percentage, ctax_name, ctax_percentage,
        createOpen, editOpen, deleteOpen, createInput,
        openCreate, closeCreate, openEdit, closeEdit, openDelete, closeDelete,
        createTax, updateTax, deleteTax,
        permission, canEdit, canDelete,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub     { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
  
  /* ── Search ──────────────────────────────────────────────────────────── */
  .search-wrap  { position:relative; display:flex; align-items:center; }
  .search-icon  { position:absolute; left:11px; color:#7a90b0; font-size:13px; pointer-events:none; }
  .search-input { padding:8px 12px 8px 33px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; width:210px; outline:none; transition:border-color .15s; }
  .search-input:focus { border-color:#004AAD; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
  .btn-primary-action:hover:not(:disabled):not(.btn--disabled) { background:#003b8e; }
  .btn-primary-action:disabled, .btn--disabled { opacity:.45; pointer-events:none; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  .btn-danger-action { display:inline-flex; align-items:center; gap:7px; background:#e03535; color:#fff; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-danger-action:hover { background:#c02828; }
  .mt-3 { margin-top:12px; }
  .me-1 { margin-right:4px; }
  
  /* ── Data card ───────────────────────────────────────────────────────── */
  .data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── States ──────────────────────────────────────────────────────────── */
  .state-block      { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
  .state-icon       { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
  .state-icon--blue { background:rgba(0,74,173,.08); color:#004AAD; }
  .state-icon--red  { background:#fff0f0; color:#e03535; }
  .state-title      { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .state-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .text-muted-cell  { color:#4a5e78; }
  
  /* ── Table ───────────────────────────────────────────────────────────── */
  .table-wrap  { overflow-x:auto; }
  .data-table  { width:100%; border-collapse:collapse; }
  .data-table thead tr { background:#f8fafc; }
  .data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
  .data-table tbody tr:last-child { border-bottom:none; }
  .data-table tbody tr:hover { background:#f8fafc; }
  .data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
  .text-center { text-align:center; }
  .col-actions { width:90px; text-align:center; }
  
  /* ── Tax cell ────────────────────────────────────────────────────────── */
  .tax-cell { display:flex; align-items:center; gap:10px; }
  .tax-icon { width:30px; height:30px; border-radius:7px; background:#fff7ed; color:#e08c00; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
  .tax-name { font-size:13px; font-weight:500; color:#1a2b45; }
  
  /* ── Rate badge ──────────────────────────────────────────────────────── */
  .rate-badge { display:inline-flex; align-items:center; background:#f0fdf4; color:#16a34a; border:1px solid #bbf7d0; border-radius:6px; padding:3px 10px; font-size:13px; font-weight:700; }
  
  /* ── Action buttons ──────────────────────────────────────────────────── */
  .actions-cell       { display:flex; align-items:center; justify-content:center; gap:6px; }
  .action-btn         { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; color:#7a90b0; cursor:pointer; transition:all .15s; }
  .action-btn--edit:hover:not(:disabled):not(.action-btn--disabled)   { background:#e8f4fd; color:#004AAD; border-color:#c5d6f5; }
  .action-btn--delete:hover:not(:disabled):not(.action-btn--disabled) { background:#fff0f0; color:#e03535; border-color:#f5c6c6; }
  .action-btn--disabled { opacity:.35; pointer-events:none; }
  
  /* ── Modal ───────────────────────────────────────────────────────────── */
  .modal-overlay         { position:fixed; inset:0; z-index:1050; display:flex; align-items:center; justify-content:center; opacity:0; pointer-events:none; transition:opacity .2s; }
  .modal-overlay.open    { opacity:1; pointer-events:all; }
  .modal-backdrop-custom { position:fixed; inset:0; z-index:1040; background:rgba(15,25,50,.45); backdrop-filter:blur(2px); }
  .modal-box             { background:#fff; border-radius:14px; width:100%; max-width:460px; margin:16px; box-shadow:0 20px 60px rgba(0,0,0,.18); overflow:hidden; transform:translateY(16px); transition:transform .22s ease; z-index:1051; position:relative; }
  .modal-overlay.open .modal-box { transform:translateY(0); }
  .modal-header-styled   { padding:20px 24px 16px; border-bottom:1px solid #e4eaf2; display:flex; align-items:center; justify-content:space-between; }
  .modal-title-styled    { font-size:16px; font-weight:700; color:#1a2b45; }
  .modal-close-btn       { width:30px; height:30px; border-radius:7px; border:1px solid #dde5f0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#7a90b0; font-size:13px; transition:background .15s; }
  .modal-close-btn:hover { background:#f4f7fb; }
  .modal-body-styled     { padding:20px 24px; display:flex; flex-direction:column; gap:16px; }
  .modal-footer-styled   { padding:16px 24px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; gap:10px; }
  
  /* ── Modal form ──────────────────────────────────────────────────────── */
  .fields-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-group-styled  { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req                { color:#e03535; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  
  /* ── Percentage input ────────────────────────────────────────────────── */
  .input-with-suffix { position:relative; display:flex; align-items:center; }
  .input-with-suffix .form-input-styled { padding-right:36px; }
  .input-suffix { position:absolute; right:12px; font-size:13px; font-weight:700; color:#7a90b0; pointer-events:none; }
  
  /* ── Delete modal ────────────────────────────────────────────────────── */
  .delete-icon-wrap { width:64px; height:64px; border-radius:50%; background:#fff0f0; color:#e03535; display:flex; align-items:center; justify-content:center; margin:0 auto 16px; }
  .delete-title     { font-size:17px; font-weight:700; color:#1a2b45; margin-bottom:8px; }
  .delete-sub       { font-size:13px; color:#7a90b0; line-height:1.5; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:768px) {
    .page-header, .page-actions { flex-direction:column; align-items:stretch; }
    .search-input { width:100%; }
    .fields-row   { grid-template-columns:1fr; }
  }
  </style>